// LocalStorage-backed cache for device system_type values.
// Shape: { entries: { coreid: { system_type: string, productid: string, saved_at: ISOString } }, version: 1 }
// TTL default: 7 days.

const STORAGE_KEY = 'ssd_system_type_cache_v1';
const VERSION = 1;
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function now() { return Date.now(); }

function readRaw() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.warn('systemTypeCache: unable to parse cache', e);
    return null;
  }
}

function writeRaw(obj) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (e) {
    // Quota or other error; ignore gracefully
    console.warn('systemTypeCache: write failed', e);
  }
}

function initIfNeeded(existing) {
  if (existing && existing.version === VERSION && existing.entries) return existing;
  return { version: VERSION, entries: {} };
}

export function loadCache() {
  return initIfNeeded(readRaw());
}

export function saveCache(cache) {
  writeRaw(cache);
}

export function getSystemTypeForCore(coreid) {
  const cache = loadCache();
  if (!cache) return undefined;
  const entry = cache.entries[coreid];
  if (!entry) return undefined;
  // TTL check (if expired we treat as miss; prune lazily)
  if (entry.saved_at && (now() - new Date(entry.saved_at).getTime()) > DEFAULT_TTL_MS) {
    return undefined;
  }
  return entry.system_type;
}

export function updateFromDevice(device) {
  if (!device || !device.coreid) return;
  const sysType = device.aggregatedData && device.aggregatedData['r/system_type'];
  if (!sysType) return; // nothing to store yet
  const cache = loadCache();
  if (!cache) return; // SSR or disabled
  const prev = cache.entries[device.coreid];
  // Avoid rewrite if unchanged
  if (prev && prev.system_type === sysType) return;
  cache.entries[device.coreid] = {
    system_type: sysType,
    fw_version: device.fw_version || '',
    productid: device.productid || '',
    published_at: device.published_at || '',
    saved_at: new Date().toISOString()
  };
  saveCache(cache);
}

export function pruneTTL(ttlMs = DEFAULT_TTL_MS) {
  const cache = loadCache();
  if (!cache) return;
  let changed = false;
  Object.keys(cache.entries).forEach(cid => {
    const e = cache.entries[cid];
    if (!e.saved_at || (now() - new Date(e.saved_at).getTime()) > ttlMs) {
      delete cache.entries[cid];
      changed = true;
    }
  });
  if (changed) saveCache(cache);
}

// Eager prune on module load (safe no-op on SSR)
try { pruneTTL(); } catch (_) {}
